import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getDemographicColor, getDemographicLabel } from '../../demographics';
import { BlackTechStreetData, latest, pctChange, previous } from '../types';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function ParticipantsOverviewCard({ data }: { data: BlackTechStreetData }) {
  const theme = useMantineTheme();

  const participants = data.participants;
  const curP = latest(participants);
  const prevP = previous(participants);

  const totalAllTime = data.participantsAllTimeTotal;
  const qoq = pctChange(curP.participantsStarted, prevP?.participantsStarted ?? null);
  const qoqArrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqLabel = (n: number | null): string => (n === null ? '— QoQ' : `${qoqArrow(n)}${Math.abs(n).toFixed(2)}% QoQ`);

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(curP.quarter);
  const ytd = currentMeta
    ? participants
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.participantsStarted, 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = participants
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point.participantsStarted, 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText = yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${qoqArrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const status = { label: 'Snapshot', color: 'gray' as const };

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const raceSeries = data.race;
  const curR = latest(raceSeries);
  const prevR = previous(raceSeries);

  const raceTotals = data.raceAllTimeByRace;
  const raceKeys = Array.from(
    new Set([
      ...Object.keys(raceTotals),
      ...Object.keys(curR.byRace ?? {}),
      ...(prevR ? Object.keys(prevR.byRace ?? {}) : []),
    ]),
  );

  const raceQoqPct = raceKeys.reduce((acc, r) => {
    const c = curR.byRace[r] ?? 0;
    const p = prevR ? prevR.byRace[r] ?? 0 : 0;
    acc[r] = prevR ? (p === 0 ? (c === 0 ? 0 : null) : ((c - p) / p) * 100) : null;
    return acc;
  }, {} as Record<string, number | null>);

  const raceSegments = raceKeys.map((race, idx) => ({
    race,
    raceLabel: getDemographicLabel(race),
    total: raceTotals[race] ?? 0,
    qoq: raceQoqPct[race],
    color: getDemographicColor(theme, idx),
  }));
  const raceTotalAllTime = raceSegments.reduce((acc, s) => acc + s.total, 0);

  return (
    <CardShell
      title="ASPIRE Snapshot"
      subtitle={curP.quarter}
      statusLabel={status.label}
      statusColor={status.color}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Unique individuals who began participation this quarter (QS: 45.0).',
              value: (
                <Text size="lg" weight={800}>
                  {curP.participantsStarted}
                </Text>
              ),
              sublabel: qoqLabel(qoq),
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date participants (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'All-Time',
              labelTooltip: 'Total participants across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {totalAllTime}
                </Text>
              ),
            },
          ]}
        />

        <Stack spacing={6} sx={{ marginTop: '0.75rem' }}>
          <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2 }}>
            Participants By Race
          </Text>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 16, height: 200 }}>
            <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const p: any = payload[0]?.payload;
                    if (!p) return null;
                    const pct = raceTotalAllTime > 0 ? Math.round((p.value / raceTotalAllTime) * 100) : 0;
                    const seg = raceSegments.find((s) => s.race === p.name);
                    const qoqText = seg?.qoq == null ? '— QoQ' : `${qoqArrow(seg.qoq)}${Math.abs(seg.qoq).toFixed(2)}% QoQ`;
                    return (
                      <div style={tooltipShellStyle as any}>
                        <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                          {getDemographicLabel(p.name)}
                        </div>
                        <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                          <div style={{ fontWeight: 900, fontSize: 14 }}>{p.value}</div>
                          <div style={{ opacity: 0.9 }}>{pct}% of participants (all-time)</div>
                          <div style={{ opacity: 0.9 }}>{qoqText}</div>
                        </div>
                      </div>
                    );
                  }}
                  contentStyle={{
                    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
                    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
                    borderRadius: 10,
                    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
                    padding: '10px 12px',
                  }}
                  labelStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                  itemStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                />
                <Pie
                  data={raceSegments.filter((s) => s.total > 0).map((s) => ({ name: s.race, value: s.total }))}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={1}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {raceSegments
                    .filter((s) => s.total > 0)
                    .map((s) => (
                      <Cell key={s.race} fill={s.color} stroke="none" />
                    ))}
                </Pie>
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{
                    fontSize: 12,
                    fill: theme.colorScheme === 'dark' ? '#fff' : '#000',
                    fontWeight: 600,
                  }}
                >
                  All-Time
                </text>
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box
              sx={{
                width: '50%',
                display: 'grid',
                gridTemplateColumns: raceSegments.filter((s) => s.total > 0).length > 4 ? '1fr 1fr' : '1fr',
                gap: '6px 8px',
                alignContent: 'center',
              }}
            >
              {raceSegments
                .filter((s) => s.total > 0)
                .map((seg) => (
                  <Box key={`legend-${seg.race}`} sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: seg.color,
                        flex: '0 0 auto',
                      }}
                    />
                    <Text size="xs" color="dimmed">
                      {seg.raceLabel}
                    </Text>
                  </Box>
                ))}
            </Box>
          </Box>
        </Stack>

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Quarterly Participants
        </Text>

        <Box sx={{ height: 150 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={participants}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
              <XAxis
                dataKey="quarter"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const v = payload[0]?.value;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                        {typeof v === 'number' ? v : '—'} Participants
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="participantsStarted"
                name="Participants"
                fill={theme.colors.blue[6]}
                  barSize={18}
                radius={[6, 6, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
